import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_URL } from "@/components/base/Seo";
import type { Category } from "@/types/directory";
import { getCategoryBySlug } from "@/services/directoryService";
import { buildCategoryDescription } from "@/utils/categoryMatch";
import BusinessExplorer from "@/components/feature/BusinessExplorer";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getCategoryBySlug(slug)
      .then((result) => {
        if (active) setCategory(result);
      })
      .catch(() => {
        if (active) setCategory(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (!loading && !category) {
    return (
      <SiteLayout>
        <section className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center px-6 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-apps-2-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            Category not found
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            We could not find that category. Browse all available categories to keep exploring.
          </p>
          <Link
            to="/categories"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            View all categories
          </Link>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Seo
        title={`${category?.name ?? "Businesses"} in Coastal Karnataka`}
        description={
          category
            ? buildCategoryDescription(category.name, category.description)
            : "Discover verified businesses in this category across South Canara, Udupi and North Canara in coastal Karnataka."
        }
        keywords={`${category?.name ?? "businesses"} Karnataka, ${category?.name ?? "businesses"} Mangaluru, ${category?.name ?? "businesses"} Udupi, coastal Karnataka directory`}
        path={`/category/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${category?.name ?? "Businesses"} in Coastal Karnataka`,
          url: `${SITE_URL}/category/${slug}`,
        }}
      />
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <Link to="/categories" className="transition-colors hover:text-primary-700">
              Categories
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">{category?.name ?? slug}</span>
          </nav>

          <div className="mt-4 flex flex-col gap-5 md:flex-row md:items-center">
            {category ? (
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className={`${category.icon} text-2xl`} />
              </span>
            ) : null}
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
                {category?.name ?? "Loading category..."}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {category
                  ? buildCategoryDescription(category.name, category.description)
                  : "Discover verified businesses in this category across coastal Karnataka."}
              </p>
              {category ? (
                <p className="mt-3 flex items-center gap-2 text-sm font-medium text-secondary-700">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-store-3-line text-base" />
                  </span>
                  {category.businessCount.toLocaleString("en-IN")} businesses in this category
                </p>
              ) : null}
            </div>
          </div>

          {category && category.subcategories.length > 0 ? (
            <div className="mt-6 flex flex-wrap gap-2">
              {category.subcategories.map((subcategory) => (
                <span
                  key={subcategory.slug}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700"
                >
                  {subcategory.name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
        <BusinessExplorer lockedCategory={slug} showCategoryFilter={false} pageSize={9} showPlacements />
      </section>
    </SiteLayout>
  );
}